import moment from 'moment';
import {
  SERVICE_CHANGE_VALUE,
  SERVICE_CHANGE_VALUES,
  SERVICE_CHANGE_CURRENT_SR,
  SERVICE_SET_LEAD_DETAIL,
  SERVICE_SET_ITEM,
} from '@shared/store/constants/mutationTypes';

import apiClient from '@shared/lib/general/apiClient';
import ServiceReference from '@shared/lib/constant/ServiceReference';
import ActionStatus from '@shared/lib/constant/ActionStatus';

const getDateRange = (value) => {
  let startDate;
  let endDate;

  switch (value) {
    case 'lastWeek':
      startDate = moment().subtract(1, 'week').startOf('isoWeek');
      endDate = moment().subtract(1, 'week').endOf('isoWeek');
      break;

    case 'lastMonth':
      startDate = moment().subtract(1, 'month').startOf('month');
      endDate = moment().subtract(1, 'month').endOf('month');
      break;

    case 'lastYear':
      startDate = moment().subtract(1, 'year').startOf('year');
      endDate = moment().subtract(1, 'year').endOf('year');
      break;

    default:
      return null;
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  };
};

const makeAPIQuery = (filters, activeTab, currentPosition) => {
  const query = {};
  filters.forEach((option) => {
    const { key, active, activeOption, selectedDateRange } = option;
    if (active) {
      const { value } = activeOption;
      if (key === 'sortBy') {
        query.sort = value;
        if (value === 'distance' && currentPosition && currentPosition?.coords) {
          const {
            coords: { latitude, longitude },
          } = currentPosition;
          query.userLat = latitude;
          query.userLon = longitude;
        }
      }
      if (key === 'unitAge') {
        query.ageGroup = value;
      }
      if (key === 'lastOccurrence') {
        query.occurrenceGroup = activeOption.value;
      }
      if (key === 'leadType' && Array.isArray(activeOption)) {
        query.alertGroups = activeOption.map((item) => item.value);
      }
      if (key === 'status') {
        query.status = activeOption.value;
      }
      if (key === 'visitType') {
        query.type = activeOption.value;
      }
      if (key === 'serviceCategory') {
        query.serviceCategory = activeOption.value;
      }
      if (activeTab === 'scheduled') {
        if (key === 'serviceType') {
          query.type = value;
        }
        if (key === 'techs') {
          query.assignedTechId = value;
        }
      }
      if (activeTab === 'incomplete') {
        if (key === 'serviceStatus') {
          query.serviceStatus = activeOption.value;
        }
      }
      if (activeTab === 'closed') {
        if (key === 'statusClosedTab') {
          query.serviceStatus = activeOption.value;
        }
      }
      if (key === 'dateCreated') {
        if (
          activeOption.value === 'custom' &&
          selectedDateRange.start != null &&
          selectedDateRange.end != null
        ) {
          query.startDate = moment(selectedDateRange.start).format('YYYY-MM-DD');
          query.endDate = moment(selectedDateRange.end).format('YYYY-MM-DD');
        } else {
          const { startDate, endDate } = getDateRange(value);
          query.startDate = startDate;
          query.endDate = endDate;
        }
      }
    }
  });
  return query;
};

function toggleLoader(loader, value, callback) {
  callback({
    type: SERVICE_CHANGE_VALUE,
    field: loader,
    value,
  });
}

function showToast(status, message, callback, bgColor, fontColor, action) {
  callback('SET_SNACKBAR', {
    show: true,
    header: status,
    message,
    bgColor,
    fontColor,
    action,
    status,
  });
}

function makeToast(status) {
  const toast = {};
  if (status === ActionStatus.Scheduled) {
    toast.bgColor = '#4CD87D';
    toast.fontColor = '#000';
    toast.term = 'is now a ';
    toast.endTerm = ' Job!';
    toast.contentColor = '#000';
  } else if (
    status === ActionStatus.Cancelled ||
    status === ActionStatus.Resolved ||
    status === ActionStatus.Completed
  ) {
    toast.bgColor = '#B8BCC7';
    toast.fontColor = '#000';
    toast.term = 'has been ';
    toast.endTerm = '!';
    toast.contentColor = '#000';
  } else if (status === ActionStatus.Snoozed || status === ActionStatus.Dismissed) {
    toast.bgColor = '#0B0F1E';
    toast.fontColor = '#FFF';
    toast.term = 'is now ';
    toast.endTerm = '!';
  } else if (status === ActionStatus.Updated) {
    toast.bgColor = '#4CD87D';
    toast.fontColor = '#000';
    toast.term = 'has been ';
    toast.endTerm = '!';
    toast.contentColor = '#000';
  } else {
    toast.bgColor = '#2759E4';
    toast.fontColor = '#FFF';
  }
  return toast;
}

function handleToast(data, status, callback, serviceRef) {
  const { serviceAddress } = data;
  const { address1, zip } = serviceAddress;
  const toast = makeToast(status);
  const toastMessage = `${address1}, ${zip} ${toast.term} `;

  const detailView = ServiceReference.LEAD === serviceRef ? 'smart-lead' : 'service-request';
  const changes = [
    {
      field: 'serviceReference',
      value: serviceRef,
    },
    {
      field: 'showServiceBottomSheet',
      value: true,
    },
    {
      field: 'serviceBottomsheetView',
      value: detailView,
    },
    {
      field: 'serviceInfo',
      value: data,
    },
  ];
  callback('SET_SNACKBAR', {
    show: true,
    header: '',
    message: toastMessage,
    bgColor: toast.bgColor,
    contentColor: toast.contentColor,
    timer: 10,
    fontColor: toast.fontColor,
    action: {
      name: `${status}${toast.endTerm}`,
      handler: () =>
        callback({
          type: SERVICE_CHANGE_VALUES,
          changes,
        }),
    },
  });
}

const actions = {
  changeServiceValue({ commit }, payload) {
    const { field, value } = payload;

    commit({
      type: SERVICE_CHANGE_VALUE,
      field,
      value,
    });
  },
  changeServiceValues({ commit }, payload) {
    commit({
      type: SERVICE_CHANGE_VALUES,
      changes: payload,
    });
  },
  async setInvoice({ commit }, formData) {
    try {
      toggleLoader('uploadingInvoice', true, commit);
      const { data: invoiceUrl } = await apiClient({
        method: 'POST',
        url: '/service/job/invoice',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return invoiceUrl;
    } catch (err) {
      commit('SET_SNACKBAR', {
        show: true,
        color: 'error',
        message: err,
      });
      return null;
    } finally {
      toggleLoader('uploadingInvoice', false, commit);
    }
  },
  async getInvoiceResponse(_, url) {
    const { data } = await apiClient({
      url: '/service/job/doc/response',
      method: 'PUT',
      data: {
        url,
      },
    });
    return data;
  },
  clearServiceFilters({ commit, state }) {
    const { serviceFilters } = state;

    const updatedFilters = serviceFilters.map((filter) => ({
      ...filter,
      active: false,
      activeOption: filter.multiple ? [] : {},
    }));

    commit({
      type: SERVICE_CHANGE_VALUE,
      field: 'serviceFilters',
      value: updatedFilters,
    });
  },
  async getServiceJobByRef({ commit, state }, payload) {
    try {
      const { ref, refId } = payload;
      const { isActioned } = state;

      toggleLoader('loadingDetail', true, commit);
      const { data } = await apiClient({
        method: 'GET',
        url: `/service/job/query/${ref}/${refId}`,
      });

      const changes = [
        {
          field: 'serviceBottomsheetView',
          value: 'service-request',
        },
        {
          field: 'serviceInfo',
          value: data,
        },
        {
          field: 'isActioned',
          value: !isActioned,
        },
      ];
      commit({
        type: SERVICE_CHANGE_VALUES,
        changes,
      });
      handleToast(data, ActionStatus.Scheduled, commit, ServiceReference.SERVICE_JOB);
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingDetail', false, commit);
    }
  },
  async updateServiceInfo({ commit, state, rootState }, actionReq) {
    const { serviceInfo, isActioned, isNoteActioned } = state;

    const {
      technician: { technicians },
    } = rootState;

    if (serviceInfo.assignedTechId !== actionReq.serviceTechId) {
      const techFound = technicians.find((tech) => tech.id === actionReq.serviceTechId);
      if (techFound) {
        actionReq.assignedTech = `${techFound.firstName} ${techFound.lastName}`;
      }
    }

    const updatedServiceInfo = {
      ...serviceInfo,
      ...actionReq,
      serviceStatus: actionReq.actionStatus,
      completedDate: actionReq.completedDate,
      scheduleDate: actionReq.serviceDate,
      scheduleTime: actionReq.serviceTime,
    };
    const changes = [
      {
        field: 'serviceInfo',
        value: updatedServiceInfo,
      },
      {
        field: 'isActioned',
        value: !isActioned,
      },
      {
        field: 'isNoteActioned',
        value: !isNoteActioned,
      },
    ];
    commit({
      type: SERVICE_CHANGE_VALUES,
      changes,
    });
    let serviceRef;
    let status = actionReq.actionStatus;
    if (serviceInfo.jobId) {
      serviceRef = ServiceReference.SERVICE_JOB;
      status =
        actionReq.actionStatus === ActionStatus.Scheduled
          ? ActionStatus.Updated
          : actionReq.actionStatus;
    } else if (serviceInfo.alertNoticeId) {
      serviceRef = ServiceReference.LEAD;
    } else {
      serviceRef = ServiceReference.SERVICE_REQUEST;
    }
    handleToast(updatedServiceInfo, status, commit, serviceRef);
  },
  async searchMember({}, payload) {
    try {
      const { search } = payload;
      const { data: list } = await apiClient({
        method: 'GET',
        url: `/customer/search/all?query=${search}`,
      });
      return list;
    } catch (err) {
      console.error(err);
    }
    return [];
  },
  changeCurrentSRValue({ commit }, payload) {
    const { field, value } = payload;

    commit({
      type: SERVICE_CHANGE_CURRENT_SR,
      field,
      value,
    });
  },
  async getServiceCategoriesByCorp({ rootState, commit }) {
    const {
      admin: {
        corporation: { id: corporationId },
      },
    } = rootState;
    const { data: categoryList } = await apiClient({
      method: 'GET',
      url: `/services/category/list/${corporationId}`,
    });

    commit({
      type: SERVICE_CHANGE_VALUE,
      field: 'serviceCategoryList',
      value: categoryList,
    });
  },
  async getVisitTypes({ commit }, category) {
    const { data: visitList } = await apiClient({
      method: 'GET',
      url: `/services/visittype/list/${category}`,
    });
    commit({
      type: SERVICE_CHANGE_VALUE,
      field: 'visitTypeList',
      value: visitList,
    });
  },
  async getServiceConditions({ commit }, payload) {
    const { visitType, category } = payload;

    const { data: conditionList } = await apiClient({
      method: 'POST',
      url: '/services/condition/list',
      data: {
        category,
        visitType,
      },
    });
    commit({
      type: SERVICE_CHANGE_VALUE,
      field: 'conditionList',
      value: conditionList,
    });
  },
  async getAllServiceRequest({ commit, rootState }, requestId) {
    try {
      toggleLoader('loadingServiceRequest', true, commit);
      const {
        admin: {
          adminFilter: { corpId },
        },
        service: { serviceFilters, activeTab, currentPosition },
      } = rootState;

      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'loadingServiceRequest',
        value: true,
      });

      const query = {
        ...makeAPIQuery(serviceFilters, activeTab, currentPosition),
        corpId,
      };

      if (!query.serviceStatus) {
        query.serviceStatus = activeTab;
      }

      const { data } = await apiClient({
        method: 'POST',
        url: `/services/request/query/${corpId}`,
        data: query,
      });

      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'serviceRequestList',
        value: data,
      });

      if (requestId) {
        const serviceItem = data?.find((sr) => sr.requestId === Number(requestId));
        if (serviceItem) {
          commit({
            type: SERVICE_SET_ITEM,
            serviceInfo: serviceItem,
            serviceReference: ServiceReference.SERVICE_REQUEST,
            bottomSheetView: 'service-request',
          });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingServiceRequest', false, commit);
    }
  },
  async getAllLeads({ commit, rootState, getters }, leadId) {
    try {
      toggleLoader('loadingLeads', true, commit);
      const {
        admin: {
          adminFilter: { corpId },
        },
        service: { activeTab, serviceFilters, currentPosition },
      } = rootState;

      const { isSmacAdmin } = getters;

      const queryLeadFilter = {
        ...makeAPIQuery(serviceFilters, activeTab, currentPosition),
        corpId,
        publish: !(isSmacAdmin && leadId),
      };

      if (!queryLeadFilter.serviceStatus) {
        queryLeadFilter.serviceStatus = activeTab;
      }

      const { data } = await apiClient({
        method: 'POST',
        url: `/alertnotice/query/${corpId}`,
        data: queryLeadFilter,
      });

      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'leadList',
        value: data,
      });
      if (leadId) {
        const serviceItem = data?.find((lead) => lead.id === Number(leadId));
        if (serviceItem) {
          commit({
            type: SERVICE_SET_ITEM,
            serviceInfo: serviceItem,
            serviceReference: ServiceReference.LEAD,
            bottomSheetView: 'smart-lead',
          });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingLeads', false, commit);
    }
  },
  async getAllServiceJobs({ commit, rootState }, requestId) {
    try {
      toggleLoader('loadingJobs', true, commit);
      const {
        admin: {
          adminFilter: { corpId },
        },
        service: { activeTab, serviceFilters, currentPosition },
      } = rootState;

      const queryJobFilter = {
        ...makeAPIQuery(serviceFilters, activeTab, currentPosition),
        corpId,
      };

      if (!queryJobFilter.serviceStatus) {
        queryJobFilter.serviceStatus = activeTab;
      }

      const { data } = await apiClient({
        method: 'POST',
        url: `/service/job/query/${corpId}`,
        data: queryJobFilter,
      });

      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'jobList',
        value: data,
      });
      if (requestId) {
        const serviceItem = data?.find((j) => j.serviceRequestId === Number(requestId));
        if (serviceItem) {
          commit({
            type: SERVICE_SET_ITEM,
            serviceInfo: serviceItem,
            serviceReference: ServiceReference.SERVICE_JOB,
            bottomSheetView: 'service-request',
          });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingJobs', false, commit);
    }
  },
  async exportLeadsList({ rootState }) {
    const {
      admin: {
        adminFilter: { corpId },
      },
    } = rootState;
    try {
      const url = `/alertnotice/report/${corpId}`;
      const { data } = await apiClient({
        method: 'GET',
        url,
      });
      return data;
    } catch (err) {
      console.error(err);
    }
    return false;
  },
  async dismissAlertForService({ commit, state }, payload) {
    try {
      const { alert, actionStatus, actionReason, actionNote } = payload;

      const { activeAlertList, systemInsightList } = state;
      const dismissAlerts = [alert].map((a) => ({
        alertId: a.alertId,
        status: actionStatus,
        actionReason,
        note: actionNote,
      }));
      await apiClient({
        method: 'PUT',
        url: 'alerts/action/perform',
        data: dismissAlerts,
      });
      if (alert.severity?.toLowerCase() === 'watching') {
        const updatedList = systemInsightList.filter(
          (item) => item.alert.alertId !== alert.alertId,
        );
        commit({
          type: SERVICE_CHANGE_VALUE,
          field: 'systemInsightList',
          value: updatedList,
        });
      } else {
        const updatedList = activeAlertList.filter((item) => item.alert.alertId !== alert.alertId);
        commit({
          type: SERVICE_CHANGE_VALUE,
          field: 'activeAlertList',
          value: updatedList,
        });
      }
    } catch (err) {
      throw err;
    }
  },
  async setChronicAlertsForService({ state }, dismissFor) {
    const { serviceInfo, selectedAlert, chronicType, chronicNote } = state;

    const unmuteDate = moment().add(dismissFor, 'days').format('YYYY-MM-DD');
    let chronicList;
    if (chronicType === 'lead') {
      const { data } = await apiClient({
        method: 'GET',
        url: `/alertnotice/detail/id/${serviceInfo.alertNoticeId}`,
      });
      chronicList = data.alertNotice.alerts;
    } else if (chronicType === 'alert') {
      chronicList = selectedAlert.alertId ? [selectedAlert] : null;
    }
    const dismissAlerts =
      chronicList &&
      chronicList.map((a) => ({
        alertId: a.alertId,
        status: 'Dismissed_Chronic',
        note: chronicNote,
        unmuteDate,
      }));

    await apiClient({
      method: 'PUT',
      url: 'chronicsystem/set',
      data: dismissAlerts,
    });
  },
  async getAllTechnicians({ dispatch, rootState, getters }) {
    const {
      admin: {
        current: { corporationId },
        adminFilter: { corpId },
      },
      service: { serviceFilters },
    } = rootState;

    const { isSmacAdmin } = getters;

    const finalCorpId = isSmacAdmin ? corpId : corporationId;

    try {
      const { data } = await apiClient({
        method: 'GET',
        url: `/technicians/all/${finalCorpId}`,
      });

      const newFilters = serviceFilters.map((filter) => {
        const newFilter = {
          ...filter,
        };
        if (filter.key === 'techs') {
          const techOptions = data
            .filter((item) => !item.nonInstaller)
            .map((tech) => {
              const option = {
                label: `${tech.firstName} ${tech.lastName}`,
                value: tech.id,
              };
              return option;
            });
          newFilter.options = techOptions.sort((a, b) => {
            if (a.label.toLowerCase() > b.label.toLowerCase()) {
              return 1;
            }
            if (a.label.toLowerCase() < b.label.toLowerCase()) {
              return -1;
            }
            return 0;
          });
          newFilter.activeOption = {};
        }
        return newFilter;
      });

      dispatch('changeServiceValue', {
        field: 'serviceFilters',
        value: newFilters,
      });
      return data;
    } catch (err) {
      console.error(err);
    }
    return [];
  },
  async createServiceJob({ commit, state }, newJob) {
    try {
      const { isActioned } = state;

      const request = {
        customerId: newJob.customerId,
        corpId: newJob.corpId,
        customerNotes: newJob.customerNotes,
        serviceIssues: newJob.serviceIssues,
        serviceType: newJob.serviceType,
        serviceAddressId: newJob.serviceAddressId,
        scheduleDate: moment(newJob.scheduleDate).format('YYYY-MM-DD'),
        scheduleTime: newJob.scheduleTime,
        actionStatus: ActionStatus.Scheduled,
        requestCategory: newJob.serviceCategory,
        actionStatusDate: moment().toISOString(),
        serviceTechId: newJob.assignedTechId,
      };

      const { data } = await apiClient({
        method: 'PUT',
        url: '/service/job/create',
        data: request,
      });

      const changes = [
        {
          field: 'serviceInfo',
          value: data,
        },
        {
          field: 'isActioned',
          value: !isActioned,
        },
      ];
      commit({
        type: SERVICE_CHANGE_VALUES,
        changes,
      });

      handleToast(data, ActionStatus.Scheduled, commit, ServiceReference.SERVICE_JOB);
    } catch (error) {
      showToast('error', `Failed to create service job. Please try again later - ${error}`, commit);
    } finally {
      toggleLoader('processing', false, commit);
    }
  },
  async updateAlertNoticeAction({ commit, dispatch }, payload) {
    try {
      toggleLoader('processing', true, commit);
      const {
        alertNoticeId,
        actionStatus,
        actionReason,
        serviceType,
        serviceCategory,
        actionNote,
        leadDecision,
        serviceCost,
        scheduleTime,
        scheduleDate,
        snoozeDay,
        customSnoozeDay,
        assignedTechId,
        corpId,
        corpName,
      } = payload;

      const actionStatusDate = moment().toISOString();

      let snoozeUntil;
      if (snoozeDay === 'custom') {
        snoozeUntil = moment().add(customSnoozeDay, 'days').format('YYYY-MM-DD');
      } else if (snoozeDay) {
        snoozeUntil = moment().add(snoozeDay, 'days').format('YYYY-MM-DD');
      }

      let serviceDate;
      if (actionStatus === ActionStatus.Snoozed) {
        serviceDate = snoozeUntil;
      } else if (scheduleDate) {
        serviceDate = moment(scheduleDate).format('YYYY-MM-DD');
      }

      const actionReq = {
        alertNoticeId,
        actionReason,
        serviceType,
        serviceCategory,
        actionStatus,
        snoozeUntil,
        actionStatusDate,
        serviceTechId: assignedTechId || null,
        serviceCorpId: corpId,
        corpName,
        corpId,
        serviceDate,
        serviceTime: scheduleTime,
        serviceCost,
        actionNote,
        leadDecision,
      };

      await apiClient({
        method: 'PUT',
        url: `/alertnotice/id/${alertNoticeId}`,
        data: actionReq,
      });

      if (actionStatus === ActionStatus.Scheduled) {
        dispatch('getServiceJobByRef', {
          ref: 'Lead',
          refId: alertNoticeId,
        });
      } else {
        dispatch('updateServiceInfo', actionReq);
      }
    } catch (err) {
      showToast('error', `Failed to update lead. Please try again later - ${err}`, commit);
    } finally {
      toggleLoader('processing', false, commit);
    }
  },
  async updateServiceJob({ commit, dispatch }, actionJob) {
    try {
      toggleLoader('processing', true, commit);
      const { scheduleTime } = actionJob;
      let { scheduleDate } = actionJob;
      let serviceTime = scheduleTime;
      if (serviceTime?.toLowerCase().includes('am') || serviceTime?.toLowerCase().includes('pm')) {
        const [start] = serviceTime.split('-');
        serviceTime = moment(start.trim(), 'ha').format('HH:mm:ss');
      } else if (!scheduleTime && actionJob.requestedDate) {
        serviceTime = moment(actionJob.requestedDate)
          .startOf('day')
          .add(actionJob.requestedTime, 'hours')
          .format('HH:mm:ss');
      }
      if (actionJob.actionStatus === ActionStatus.Completed) {
        scheduleDate = actionJob.serviceDate;
      }

      const actionReq = {
        serviceRequestId: actionJob.requestId,
        jobId: actionJob.jobId,
        actionStatusDate: moment().toISOString(),
        actionStatus: actionJob.actionStatus,
        serviceDate: moment(scheduleDate).format('YYYY-MM-DD'),
        serviceTime,
        serviceTechName: actionJob.serviceTechName,
        serviceTechId: actionJob.assignedTechId || null,
        customerNotes: actionJob.customerNotes,
        serviceCorpId: actionJob.corpId,
        requestCategory: actionJob.serviceCategory,
        serviceType: actionJob.serviceType,
        serviceIssues: actionJob.serviceIssues,
        serviceCost: actionJob.serviceCost,
        actionNote: actionJob.actionNote,
        workPerformed: actionJob.workPerformed,
        invoiceUrl: actionJob.invoiceUrl,
      };

      await apiClient({
        method: 'PUT',
        url: 'service/job/action',
        data: actionReq,
      });
      dispatch('updateServiceInfo', actionReq);
    } catch (err) {
      showToast('error', `Failed to update service job. Please try again later - ${err}`, commit);
    } finally {
      toggleLoader('processing', false, commit);
    }
  },
  async removeServiceJobInvoice({ commit, state }) {
    try {
      const { serviceInfo } = state;

      const serviceJobUpdated = {
        ...serviceInfo,
        invoiceUrl: '',
      };

      await apiClient({
        method: 'PUT',
        url: 'service/job/update',
        data: serviceJobUpdated,
      });
      commit({
        type: SERVICE_CHANGE_VALUES,
        changes: [
          {
            field: 'serviceInfo',
            value: serviceJobUpdated,
          },
        ],
      });
    } catch (err) {
      showToast('error', `Failed to update service job. Please try again later - ${err}`, commit);
    }
  },
  async updateServiceRequest({ commit, dispatch, state }, request) {
    try {
      toggleLoader('processing', true, commit);

      const { serviceInfo } = state;

      const actionReq = {
        customerId: request.customerId,
        requestId: request.requestId,
        serviceTechId: request.assignedTechId || null,
        serviceCorpId: request.corpId,
        corpId: request.corpId,
        actionStatusDate: moment().toISOString(),
        actionStatus: request.actionStatus,
        statusNote: request.statusNote,
        serviceDate: moment(request.scheduleDate).format('YYYY-MM-DD'),
        serviceTime: request.scheduleTime,
        customerNotes: request.customerNotes,
        serviceIssues: request.serviceIssues,
        serviceCost: request.serviceCost,
        actionNote: request.actionNote,
        workPerformed: request.workPerformed,
        serviceType: request.serviceType,
        serviceCategory: request.serviceCategory,
      };

      await apiClient({
        method: 'PUT',
        url: `services/request/action/${request.requestId}`,
        data: actionReq,
      });

      if (actionReq.actionStatus === ActionStatus.Scheduled) {
        dispatch('getServiceJobByRef', {
          ref: 'Request',
          refId: actionReq.requestId,
        });
      } else {
        dispatch('updateServiceInfo', actionReq);
      }

      const serviceRef =
        actionReq.actionStatus === ActionStatus.Scheduled
          ? ServiceReference.SERVICE_JOB
          : ServiceReference.SERVICE_REQUEST;
      handleToast(serviceInfo, actionReq.actionStatus, commit, serviceRef);
    } catch (err) {
      toggleLoader('processing', false, commit);
      showToast(
        'error',
        `Failed to update service request. Please try again later - ${err}`,
        commit,
      );
    } finally {
      toggleLoader('processing', false, commit);
    }
  },
  async createServiceNote({ state, commit }, noteRequest) {
    try {
      const { serviceReference, serviceInfo, isNoteActioned } = state;
      let url;
      if (serviceReference === ServiceReference.LEAD) {
        const { alertNoticeId } = serviceInfo;
        url = `/alertnotice/id/${alertNoticeId}`;
        noteRequest.alertNoticeId = alertNoticeId;
      } else if (serviceReference === ServiceReference.SERVICE_REQUEST) {
        const { requestId } = serviceInfo;
        url = `/services/request/action/${requestId}`;
        noteRequest.requestId = requestId;
      } else if (serviceReference === ServiceReference.SERVICE_JOB) {
        url = '/service/job/action';
        noteRequest.jobId = serviceInfo.jobId;
      }
      await apiClient({
        method: 'PUT',
        url,
        data: noteRequest,
      });
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'isNoteActioned',
        value: !isNoteActioned,
      });
      showToast('success', '', commit);
    } catch (err) {
      showToast('error', `Failed to create note. Please try again later - ${err}`, commit);
    }
  },
  async getCustomerNotes({ commit }, customerId) {
    try {
      toggleLoader('loadingNotes', true, commit);
      const { data } = await apiClient({
        method: 'GET',
        url: `/customer/notes/${customerId}`,
      });
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'customerNoteList',
        value: data,
      });
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingNotes', false, commit);
    }
  },
  async updateNote({ commit, state }, noteReq) {
    try {
      const { isNoteActioned } = state;
      await apiClient({
        method: 'PUT',
        url: '/customer/notes/update',
        data: noteReq,
      });
      showToast('success', '', commit);
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'isNoteActioned',
        value: !isNoteActioned,
      });
    } catch (err) {
      showToast('error', `Failed to update note. Please try again later - ${err}`, commit);
      console.error(err);
    }
  },
  async deleteNote({ commit, state }, noteReq) {
    const { logId, reference } = noteReq;

    const { isNoteActioned } = state;
    try {
      await apiClient({
        method: 'DELETE',
        url: `/customer/notes/${reference}/delete/${logId}`,
      });
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'isNoteActioned',
        value: !isNoteActioned,
      });
      showToast('success', '', commit);
    } catch (err) {
      showToast('error', `Failed to update note. Please try again later - ${err}`, commit);
      console.error(err);
    }
  },
  async getServiceSummary({ commit, rootState }) {
    try {
      const {
        admin: {
          adminFilter: { corpId },
        },
      } = rootState;

      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'loadingSummary',
        value: true,
      });
      const { data } = await apiClient({
        method: 'GET',
        url: `/services/corp/${corpId}/summary`,
      });
      const newServiceSummary = {
        pending: 0,
        snoozed: 0,
        scheduled: 0,
        incomplete: 0,
        closed: 0,
        ...data,
      };
      commit({
        type: SERVICE_CHANGE_VALUES,
        changes: [
          {
            field: 'loadingSummary',
            value: false,
          },
          {
            field: 'serviceSummary',
            value: newServiceSummary,
          },
        ],
      });
    } catch (err) {
      console.error(err);
    }
  },
  async getLeadDetail({ commit }, payload) {
    const { noticeId, status } = payload;

    toggleLoader('loadingDetail', true, commit);

    try {
      let url = `/alertnotice/detail/id/${noticeId}`;
      if (status) {
        url = `/alertnotice/detail/id/${noticeId}?status=${status}`;
      }
      const { data } = await apiClient({
        method: 'GET',
        url,
      });

      commit({
        type: SERVICE_SET_LEAD_DETAIL,
        leadDetail: data.alertNotice,
        recentCycleList: data.cycles,
        activeAlertList: data.leadAlerts,
        systemInsightList: data.systemInsights,
      });
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingDetail', false, commit);
    }
  },
  resetLeadDetail({ commit }) {
    const changes = [
      {
        field: 'leadDetail',
        value: {},
      },
      {
        field: 'pastAlertList',
        value: [],
      },
      {
        field: 'recentCycleList',
        value: [],
      },
      {
        field: 'activeAlertList',
        value: [],
      },
      {
        field: 'systemInsightList',
        value: [],
      },
      {
        field: 'pastLeakAlertList',
        value: [],
      },
    ];
    commit({
      type: SERVICE_CHANGE_VALUES,
      changes,
    });
  },
  async getPastServices({ commit }, payload) {
    try {
      const { customerId, corpId } = payload;
      toggleLoader('loadingServices', true, commit);
      const { data: pastServices } = await apiClient({
        method: 'POST',
        url: `/service/job/query/${corpId}`,
        data: {
          corpId,
          serviceStatus: ActionStatus.Closed,
          customerId,
        },
      });
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'pastServiceList',
        value: pastServices,
      });
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingServices', false, commit);
    }
  },
  async getPastAlerts({ commit }, UID) {
    try {
      toggleLoader('loadingAlerts', true, commit);
      const { data: pastAlerts } = await apiClient({
        method: 'GET',
        url: `/alerts/uid/${UID}/history`,
      });
      commit({
        type: SERVICE_CHANGE_VALUE,
        field: 'pastAlertList',
        value: pastAlerts,
      });
    } catch (err) {
      console.error(err);
    } finally {
      toggleLoader('loadingAlerts', false, commit);
    }
  },
};

export default actions;
